import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useTheme } from 'styled-components/macro';

import BannerMessages from '../BannerMessages/BannerMessages';
import NotificationsHistory from '../NotificationsHistory/NotificationsHistory';
import ScheduleCards from '../ScheduleCards/ScheduleCards';
import DealerReportLoader from './components/DealerReportLoader';
import {
  ButtonCollapseStyled,
  CardWrapStyled,
  CardsWrapStyled,
  ClaimLabelStyled,
  ClaimReportInnerContentStyled,
  DealerReportWrapStyled,
  FlexContainerStyled,
  HeaderStyled,
  HelpBtnIconStyled,
  InnerContainerStyled,
  InnerContentRowStyled,
  InnerFlexContainerStyled,
  InvoicePercentageStyled,
  LinkStyled,
  MenuBtnStyled,
  MenuItemStyled,
  StatisticSignLargeStyled,
  StatisticSignStyled,
  getPopupContentStyle,
} from './DealerReport.styled';
import useDealerReport from './hooks/useDealerReport';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import Popup from 'components/Popup/Popup';
import Typography from 'components/Typography/Typography';
import UserInfo from 'components/UserInfo/UserInfo';
import { useFeatures } from 'contexts/FeaturesContext';
import { useOnboardingTour } from 'contexts/OnboardingTourContext';
import { useResponsive } from 'contexts/ResponsiveContext';
import { TicketsContext } from 'contexts/TicketsContext';
import { FeaturesNamesE } from 'declarations/enums/features';
import { RoutePathsE } from 'declarations/enums/routes';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import { NoopT } from 'declarations/types/common';
import { DoubleArrowUpIcon, HelpIcon } from 'icons';

const DealerReport = () => {
  /**
   * Hooks
   */
  const { isLoadingReport, claimReport } = useDealerReport();
  const { handleStartTourClick } = useOnboardingTour();
  const { isMobile } = useResponsive();
  const [isReportOpened, setReportVisibility] = useState(!isMobile);
  const menuPopupRef = useRef<PopupActions>(null);
  const theme = useTheme();
  const { isFeatureActive } = useFeatures();

  /**
   * Handlers
   */
  const handleViewReports = () => {
    setReportVisibility(!isReportOpened);
  };
  const handleMenuItemClick = useCallback(
    (cb: NoopT) => () => {
      // Close actions menu after selecting menu item
      menuPopupRef.current?.close();

      cb();
    },
    []
  );

  const { getSearchParam } = useContext(TicketsContext);

  const [clickedCardIndex, setClickedCardIndex] = useState(-1);
  const isPositive = (value: number) => value > 0;

  useEffect(() => {
    if (clickedCardIndex !== -1 && getSearchParam(TicketsRefinementKeysE.PastMonth) === null)
      setClickedCardIndex(-1);
  }, [getSearchParam, clickedCardIndex]);
  const isScheduleShown = isFeatureActive(FeaturesNamesE.Schedule);

  return (
    <DealerReportWrapStyled className="dealer-report">
      <HeaderStyled isReportOpened={isReportOpened}>
        <BannerMessages />
        <FlexContainerStyled>
          <NotificationsHistory />
          <Popup
            offsetY={4}
            arrow={false}
            ref={menuPopupRef}
            position="bottom right"
            contentStyle={getPopupContentStyle(theme, isMobile)}
            trigger={
              <HelpBtnIconStyled
                title="Start onboarding tour/show release notes"
                icon={<HelpIcon />}
              />
            }
          >
            <ul>
              <MenuItemStyled>
                <MenuBtnStyled variant="text" onClick={handleMenuItemClick(handleStartTourClick)}>
                  Onboarding tour
                </MenuBtnStyled>
              </MenuItemStyled>

              <MenuItemStyled>
                <LinkStyled to={RoutePathsE.RELEASES_PAGE}>Release notes</LinkStyled>
              </MenuItemStyled>
            </ul>
          </Popup>
          <UserInfo iconOnly={true} />
          <ButtonCollapseStyled
            size="xs"
            variant="outline"
            className="collapse-report-btn"
            onClick={handleViewReports}
            isReportOpened={isReportOpened}
            icon={<DoubleArrowUpIcon />}
            title={isReportOpened ? 'Hide report cards' : 'Show report cards'}
          >
            Collapse
          </ButtonCollapseStyled>
        </FlexContainerStyled>
      </HeaderStyled>
      {isScheduleShown && <ScheduleCards />}
      <CardsWrapStyled justify="start" align="stretch" isReportOpened={isReportOpened}>
        {isLoadingReport ? (
          <DealerReportLoader />
        ) : (
          <>
            {claimReport?.map(
              (
                {
                  totalClaimCount,
                  totalClaimAmount,
                  averageClaimAmount,
                  invoiceDateStr,
                  invoiceStartDateStr,
                  averageClaimPercentage,
                  totalRevenuePercentage,
                  totalClaimPercentage,
                },
                index
              ) => (
                <CardWrapStyled
                  isActive={false}
                  direction="column"
                  justify="start"
                  key={`dealer-${index}`}
                >
                  <ClaimReportInnerContentStyled align="start" direction="column">
                    <InnerContentRowStyled>
                      <InnerContainerStyled>
                        <Typography variant="body4" color="darkGrey">
                          {invoiceDateStr}
                        </Typography>
                        <Typography variant="h2" weight="medium">
                          $&nbsp;
                          {Math.abs(totalClaimAmount).toLocaleString('en-US', {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                          })}
                        </Typography>
                        <div>
                          <ClaimLabelStyled variant="body4" color="darkGrey">
                            Claims
                          </ClaimLabelStyled>
                          <FlexContainer justify="start">
                            <Typography color="black" size="xl">
                              {totalClaimCount}
                            </Typography>
                            {totalClaimPercentage ? (
                              <FlexContainer>
                                <StatisticSignStyled
                                  isPositive={isPositive(totalClaimPercentage)}
                                />
                                <InvoicePercentageStyled
                                  isPositive={isPositive(totalClaimPercentage)}
                                >
                                  {totalClaimPercentage.toFixed(2)}%
                                </InvoicePercentageStyled>
                              </FlexContainer>
                            ) : null}
                          </FlexContainer>
                        </div>
                      </InnerContainerStyled>
                      <InnerFlexContainerStyled>
                        {totalRevenuePercentage ? (
                          <>
                            <Typography
                              variant="body4"
                              color="darkGrey"
                              style={{
                                display: isPositive(totalRevenuePercentage) ? 'block' : 'none',
                              }}
                            >
                              From {invoiceStartDateStr}
                            </Typography>
                            <FlexContainer justify="start">
                              <StatisticSignLargeStyled
                                isPositive={isPositive(totalRevenuePercentage)}
                              />
                              <InvoicePercentageStyled
                                variant="h2"
                                weight="medium"
                                isPositive={isPositive(totalRevenuePercentage)}
                              >
                                {totalRevenuePercentage.toFixed(2)}%
                              </InvoicePercentageStyled>
                            </FlexContainer>
                          </>
                        ) : null}
                        <InnerContainerStyled>
                          <div>
                            <ClaimLabelStyled variant="body4" color="darkGrey">
                              Avg Claim
                            </ClaimLabelStyled>
                            <FlexContainer justify="start">
                              <Typography color="black" size="xl">
                                ${averageClaimAmount.toFixed(2)}
                              </Typography>
                              {averageClaimPercentage ? (
                                <FlexContainer>
                                  <StatisticSignStyled
                                    isPositive={isPositive(averageClaimPercentage)}
                                  />
                                  <InvoicePercentageStyled
                                    variant="body4"
                                    isPositive={isPositive(averageClaimPercentage)}
                                  >
                                    {averageClaimPercentage.toFixed(2)}%
                                  </InvoicePercentageStyled>
                                </FlexContainer>
                              ) : null}
                            </FlexContainer>
                          </div>
                        </InnerContainerStyled>
                      </InnerFlexContainerStyled>
                    </InnerContentRowStyled>
                  </ClaimReportInnerContentStyled>
                </CardWrapStyled>
              )
            )}
          </>
        )}
      </CardsWrapStyled>
    </DealerReportWrapStyled>
  );
};

export default DealerReport;
